import React, { useState } from "react"
import CategoryAction from "../CategoryAction/CategoryAction"
import SettingsContextMenu from "../SettingsContextMenu/SettingsContextMenu"
import SettingsContextMenuItem from "../SettingsContextMenuItem/SettingsContextMenuItem"
import SettingsButton from "../SettingsButton/SettingsButton"
import styles from "./HotKeysExtension.module.scss"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { faPen, faPlus, faTrashAlt } from "@fortawesome/pro-light-svg-icons"
import PopoverTrigger from "../PopoverTrigger/PopoverTrigger"
import HotKeysExtensionForm from "../HotKeysExtensionForm/HotKeysExtensionForm"
import { FastRepliesHotKeyWithId, HotKey } from "../../models/fastRepliesHotKeys"
import {
    useCreateHotKeyMutation,
    useDeleteHotKeyMutation,
    useUpdateHotKeyMutation
} from "../../api/controllers/fastRepliesHotKeys"
import { toHotKeyValues } from "./helpers/toHotKeyValues"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { selectFastRepliesCatalogHotKeys, selectFastRepliesOpenedElement } from "../../store/dialogs/selectors"
import { testId } from "../../utility/tests/testId/dialogs"
import { actions } from "../../store/dialogs/slice"
import { FastRepliesElementType } from "../../store/dialogs/types"
import { FastRepliesDataType, useFastRepliesData } from "./../../pages/FastRepliesSidebar/hooks/useFastRepliesData"

interface HotKeysExtensionProps {
    currentArticleId: string
    categoryId: string
    hotKey?: FastRepliesHotKeyWithId
}

const tNamespace = "dialogs:info.fast-replies.hot-keys."

const getSettings = (
    id: string,
    isHotKeyExist: boolean,
    t: TFunction,
    handleClick: () => void,
    handleDelete: () => void
) => {
    if (!isHotKeyExist) {
        return [
            <SettingsContextMenuItem
                text={t(`${tNamespace}actions.create`)}
                id={`${id}-createHotKey`}
                key={`${id}-createHotKey`}
                icon={faPlus}
                onClick={handleClick}
                testId={testId.fastRepliesHotKeyCreateAction}
            />
        ]
    } else {
        return [
            <SettingsContextMenuItem
                text={t(`${tNamespace}actions.edit`)}
                id={`${id}-editHotKey`}
                key={`${id}-editHotKey`}
                icon={faPen}
                onClick={handleClick}
                testId={testId.fastRepliesHotKeyEditAction}
                onHoverActive
            />,
            <SettingsContextMenuItem
                danger
                text={t(`${tNamespace}actions.delete`)}
                id={`${id}-deleteHotKey`}
                key={`${id}-deleteHotKey`}
                icon={faTrashAlt}
                onClick={handleDelete}
                testId={testId.fastRepliesHotKeyDeleteAction}
            />
        ]
    }
}

const HotKeysExtension: React.FC<HotKeysExtensionProps> = props => {
    const { currentArticleId, categoryId, hotKey } = props

    const { t } = useTranslation()

    const dispatch = useAppDispatch()
    const openedElement = useAppSelector(selectFastRepliesOpenedElement)

    const isPopoverOpen =
        openedElement?.articleId === currentArticleId && openedElement?.elementType === FastRepliesElementType.Popover
    const isContextMenuOpen =
        openedElement?.articleId === currentArticleId &&
        openedElement?.elementType === FastRepliesElementType.ContextMenu
    const isOtherElementOpened = openedElement?.articleId !== currentArticleId
    const canShowTooltip = openedElement === null
    const setOpenedElement = (elementType: FastRepliesElementType | null) => {
        dispatch(actions.setFastRepliesOpenedElement(elementType ? { articleId: currentArticleId, elementType } : null))
    }

    const [loading, setLoading] = useState(false)
    const [hotKeyArticleTitle, setHotKeyArticleTitle] = useState("")
    const hotKeys = useAppSelector(selectFastRepliesCatalogHotKeys)
    const resetArticleTitle = () => setHotKeyArticleTitle("")

    const [createHotKey] = useCreateHotKeyMutation()
    const [updateHotKey] = useUpdateHotKeyMutation()
    const [deleteHotKey] = useDeleteHotKeyMutation()

    const { articleNodes: articles } = useFastRepliesData(FastRepliesDataType.ARTICLES)

    const handleSetHotKey = async (hotKeyToSet: HotKey, rewrite: boolean) => {
        setLoading(true)

        // работает пока альт зафиксирован. если первая клавиша другая будет, над менять логику
        const existedHotKey = hotKeys.find(hotKey => hotKey.SecondKey === hotKeyToSet.SecondKey)
        if (existedHotKey?.ArticleSymbol && existedHotKey.ArticleSymbol !== currentArticleId && !rewrite) {
            const article = articles.find(article => article.data.symbolCode === existedHotKey.ArticleSymbol)
            const articleTitle = article?.data.title

            if (articleTitle) {
                setHotKeyArticleTitle(articleTitle)
                setLoading(false)
                return
            }
        }

        if (existedHotKey?.Id && rewrite) {
            await deleteHotKey({ id: existedHotKey.Id, articles })
            resetArticleTitle()
        }

        if (hotKey) {
            updateHotKey({
                ...hotKey,
                ...hotKeyToSet,
                articles
            }).finally(() => {
                setLoading(false)
                setOpenedElement(null)
            })
        } else {
            createHotKey({
                ...hotKeyToSet,
                ArticleSymbol: currentArticleId,
                CatalogSymbol: categoryId,
                articles
            }).finally(() => {
                setLoading(false)
                setOpenedElement(null)
            })
        }
    }

    const handleDeleteHotKey = () => {
        hotKey && deleteHotKey({ id: hotKey.Id, articles })
        setOpenedElement(null)
    }

    const items = getSettings(
        currentArticleId,
        !!hotKey,
        t,
        () => {
            setOpenedElement(FastRepliesElementType.Popover)
        },
        handleDeleteHotKey
    )

    return (
        <div className={styles.actionsContainer}>
            <span className={styles.actionsContainer__savedCombination}>{hotKey?.CombinationName}</span>
            <CategoryAction id={`${currentArticleId}-hot-key`} isHover>
                <PopoverTrigger
                    id="fast-replies-hot-key-form"
                    content={
                        <HotKeysExtensionForm
                            id={currentArticleId}
                            hidePopover={() => setOpenedElement(null)}
                            handleSetHotKey={handleSetHotKey}
                            hotKey={toHotKeyValues(hotKey)}
                            loading={loading}
                            articleTitle={hotKeyArticleTitle}
                            reset={resetArticleTitle}
                        />
                    }
                    placement="left"
                    show={isPopoverOpen}
                    className={styles.actionsContainer__hotKeyForm}
                >
                    <SettingsContextMenu show={isContextMenuOpen} placement="bottom-start" items={items}>
                        <SettingsButton
                            id={`${currentArticleId}-hot-key`}
                            tooltipText={canShowTooltip ? t(`${tNamespace}tooltip`) : ""}
                            tooltipClassName={styles.actionsContainer__tooltip}
                            onClick={() => {
                                if (isOtherElementOpened) {
                                    setOpenedElement(FastRepliesElementType.ContextMenu)
                                } else {
                                    setOpenedElement(null)
                                }
                            }}
                        />
                    </SettingsContextMenu>
                </PopoverTrigger>
            </CategoryAction>
        </div>
    )
}

export default HotKeysExtension
